import React from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Badge } from 'pages/track/Customers/components/CustomerTable/StyledComponents';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Example Table Components
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
`;

const TableHeader = styled.thead`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6'};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : props.theme.colors.blueBlack};
  vertical-align: middle;
`;

const CustomerName = styled.div`
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  margin-bottom: 2px;
`;

const CustomerEmail = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.secondary};
`;

const ActionCell = styled(TableCell)`
  text-align: right;
  width: 120px;
`;

// Eye icon for view action
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const TablesDocs = () => {
  const sampleCustomers = [
    {
      id: 1,
      name: 'John Smith',
      email: 'john@example.com',
      segment: 'Super Fan',
      segmentColor: '#22C55E',
      events: 12,
      revenue: 1250,
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah@example.com',
      segment: 'Regular Fan',
      segmentColor: '#3B82F6',
      events: 5,
      revenue: 450,
    },
    {
      id: 3,
      name: 'Mike Davis',
      email: 'mike@example.com',
      segment: 'First-Timer',
      segmentColor: '#F59E0B',
      events: 1,
      revenue: 85,
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Tables Pattern</Title>
        <Subtitle>
          Data table patterns for displaying customer lists, transactions, and
          other tabular data. Includes responsive design, hover states, and
          action buttons.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Basic Table</SectionTitle>
        <SectionDescription>
          A clean, modern table design with hover states and proper spacing.
        </SectionDescription>

        <ComponentDemo label="Basic Data Table" align="stretch">
          <TableContainer>
            <StyledTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Customer</TableHeaderCell>
                  <TableHeaderCell>Segment</TableHeaderCell>
                  <TableHeaderCell>Events</TableHeaderCell>
                  <TableHeaderCell>Revenue</TableHeaderCell>
                  <TableHeaderCell style={{ textAlign: 'right' }}>
                    Actions
                  </TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {sampleCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <CustomerName>{customer.name}</CustomerName>
                      <CustomerEmail>{customer.email}</CustomerEmail>
                    </TableCell>
                    <TableCell>
                      <Badge color={customer.segmentColor}>
                        {customer.segment}
                      </Badge>
                    </TableCell>
                    <TableCell>{customer.events}</TableCell>
                    <TableCell>${customer.revenue.toLocaleString()}</TableCell>
                    <ActionCell>
                      <ButtonV2
                        variant="icon"
                        icon={<EyeIcon />}
                        title="View"
                        onClick={() => alert(`View ${customer.name}`)}
                      />
                    </ActionCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Table Structure</SectionTitle>
        <SectionDescription>
          The basic structure for building data tables in the application.
        </SectionDescription>

        <CodeBlock>
          {`import styled from 'styled-components';

const TableContainer = styled.div\`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
\`;

const StyledTable = styled.table\`
  width: 100%;
  border-collapse: collapse;
  background: \${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
\`;

const TableHeader = styled.thead\`
  background: \${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb'};
  border-bottom: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
\`;

const TableHeaderCell = styled.th\`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  white-space: nowrap;
\`;

const TableBody = styled.tbody\`\`;

const TableRow = styled.tr\`
  border-bottom: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6'};
  transition: background-color 0.15s ease;

  &:hover {
    background: \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb'};
  }

  &:last-child {
    border-bottom: none;
  }
\`;

const TableCell = styled.td\`
  padding: 16px;
  font-size: 14px;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : props.theme.colors.blueBlack};
  vertical-align: middle;
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Customer Table Pattern</SectionTitle>
        <SectionDescription>
          The most common table pattern used in the application - displaying
          customer information with name, email, segment, and actions.
        </SectionDescription>

        <CodeBlock>
          {`import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';

const CustomerName = styled.div\`
  font-weight: 600;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  margin-bottom: 2px;
\`;

const CustomerEmail = styled.div\`
  font-size: 13px;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.secondary};
\`;

// Eye icon for view action
const EyeIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

// Usage
<StyledTable>
  <TableHeader>
    <tr>
      <TableHeaderCell>Customer</TableHeaderCell>
      <TableHeaderCell>Segment</TableHeaderCell>
      <TableHeaderCell>Events</TableHeaderCell>
      <TableHeaderCell>Revenue</TableHeaderCell>
      <TableHeaderCell>Actions</TableHeaderCell>
    </tr>
  </TableHeader>
  <TableBody>
    {customers.map((customer) => (
      <TableRow key={customer.id}>
        <TableCell>
          <CustomerName>{customer.name}</CustomerName>
          <CustomerEmail>{customer.email}</CustomerEmail>
        </TableCell>
        <TableCell>
          <Badge color={customer.segmentColor}>
            {customer.segment}
          </Badge>
        </TableCell>
        <TableCell>{customer.events}</TableCell>
        <TableCell>\${customer.revenue.toLocaleString()}</TableCell>
        <TableCell>
          <ButtonV2
            variant="icon"
            icon={<EyeIcon />}
            title="View"
            onClick={() => viewCustomer(customer.id)}
          />
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
</StyledTable>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Table with Pagination</SectionTitle>
        <SectionDescription>
          Most tables should include pagination for large datasets.
        </SectionDescription>

        <CodeBlock>
          {`const Pagination = styled.div\`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
\`;

const PageInfo = styled.div\`
  font-size: 14px;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.secondary};
\`;

const PageButtons = styled.div\`
  display: flex;
  gap: 8px;
\`;

const PageButton = styled.button\`
  padding: 6px 12px;
  border: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  background: \${(props) =>
    props.active
      ? props.theme?.colors?.primary || '#fa4616'
      : props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : '#ffffff'};
  color: \${(props) =>
    props.active
      ? '#ffffff'
      : props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : props.theme.colors.blueBlack};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: \${(props) =>
      props.active
        ? props.theme?.colors?.primary || '#fa4616'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
\`;

// Usage
<Card>
  <TableContainer>
    <StyledTable>...</StyledTable>
  </TableContainer>
  <Pagination>
    <PageInfo>
      Showing {startIndex}-{endIndex} of {totalCount}
    </PageInfo>
    <PageButtons>
      <PageButton
        onClick={handlePrevious}
        disabled={currentPage === 1}
      >
        Previous
      </PageButton>
      {pageNumbers.map((page) => (
        <PageButton
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </PageButton>
      ))}
      <PageButton
        onClick={handleNext}
        disabled={currentPage === totalPages}
      >
        Next
      </PageButton>
    </PageButtons>
  </Pagination>
</Card>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Responsive Table Pattern</SectionTitle>
        <SectionDescription>
          For mobile devices, tables should switch to a card-based layout for
          better usability.
        </SectionDescription>

        <CodeBlock>
          {`const MobileCardGrid = styled.div\`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }
\`;

const CustomerCard = styled.div\`
  background: \${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  border-radius: 12px;
  padding: 16px;
\`;

const CardHeader = styled.div\`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6'};
\`;

const DetailRow = styled.div\`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
\`;

const DetailLabel = styled.div\`
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
\`;

const DetailValue = styled.div\`
  font-weight: 500;
  color: \${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
\`;

// Usage
<>
  {/* Desktop Table */}
  <TableContainer>
    <StyledTable>...</StyledTable>
  </TableContainer>

  {/* Mobile Cards */}
  <MobileCardGrid>
    {customers.map((customer) => (
      <CustomerCard key={customer.id}>
        <CardHeader>
          <div>
            <CustomerName>{customer.name}</CustomerName>
            <CustomerEmail>{customer.email}</CustomerEmail>
          </div>
          <Badge color={customer.segmentColor}>
            {customer.segment}
          </Badge>
        </CardHeader>
        <DetailRow>
          <DetailLabel>Events</DetailLabel>
          <DetailValue>{customer.events}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Revenue</DetailLabel>
          <DetailValue>\${customer.revenue.toLocaleString()}</DetailValue>
        </DetailRow>
      </CustomerCard>
    ))}
  </MobileCardGrid>
</>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Table Action Buttons</SectionTitle>
        <SectionDescription>
          Use ButtonV2 with variant="icon" for consistent action buttons in
          tables.
        </SectionDescription>

        <CodeBlock>
          {`import ButtonV2 from 'components/ButtonV2';

// Eye icon for view action
const EyeIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

// Edit icon
const EditIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

// Delete icon
const DeleteIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

// Single action
<ButtonV2
  variant="icon"
  icon={<EyeIcon />}
  title="View"
  onClick={() => viewCustomer(id)}
/>

// Multiple actions
<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
  <ButtonV2
    variant="icon"
    icon={<EyeIcon />}
    title="View"
    onClick={() => viewCustomer(id)}
  />
  <ButtonV2
    variant="icon"
    icon={<EditIcon />}
    title="Edit"
    onClick={() => editCustomer(id)}
  />
  <ButtonV2
    variant="icon"
    icon={<DeleteIcon />}
    title="Delete"
    onClick={() => deleteCustomer(id)}
  />
</div>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use sticky headers for long tables
              <br />
              • Include pagination for large datasets
              <br />
              • Show loading skeletons while data loads
              <br />
              • Provide clear visual feedback on hover
              <br />
              • Make action buttons easily accessible
              <br />
              • Use responsive card layout on mobile
              <br />• Include empty states with helpful messages
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't show too many columns (causes horizontal scroll)
              <br />
              • Don't use tiny fonts that are hard to read
              <br />
              • Don't forget to handle loading and error states
              <br />
              • Don't make tables without clear hierarchy
              <br />
              • Don't hide important information on mobile
              <br />• Don't use excessive borders (keep it clean)
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Table Types</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Customer Tables:</strong>
            <br />• Customer list with name, email, segment, metrics
            <br />• Actions: view profile, send message, edit, delete
            <br />
            <br />
            <strong>Transaction Tables:</strong>
            <br />• Order history with date, amount, status
            <br />• Payment method, items, and order details
            <br />
            <br />
            <strong>Event Tables:</strong>
            <br />• Event list with date, name, attendance, revenue
            <br />• Actions: view details, edit event, manage tickets
            <br />
            <br />
            <strong>Activity Tables:</strong>
            <br />• User actions with timestamp, user, action type
            <br />• Filter by date range, user, or action type
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Real-World Example</SectionTitle>
        <SectionDescription>
          The CustomerTable component location:{' '}
          <code>/pages/track/Customers/components/CustomerTable/index.js</code>
        </SectionDescription>

        <CodeBlock>
          {`import { forwardRef, useImperativeHandle } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import useCustomerTable from './hooks/useCustomerTable';

// Eye icon for view action
const EyeIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const CustomerTable = forwardRef(({ accountId, searchTerm, segmentFilter }, ref) => {
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin ? '/admin-track' :
                      isEmployeeAdmin ? '/admin-employee' : '/admin';

  const {
    customers,
    loading,
    page,
    totalPages,
    totalCount,
    handlePageChange,
  } = useCustomerTable(accountId, searchTerm, segmentFilter);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getTotalCount: () => totalCount,
    getAppliedFilters: () => ({ searchTerm, segmentFilter }),
  }));

  if (loading) return <TableSkeleton />;

  return (
    <Card>
      <TableContainer>
        <StyledTable>
          <TableHeader>
            <tr>
              <TableHeaderCell>Customer</TableHeaderCell>
              <TableHeaderCell>Segment</TableHeaderCell>
              <TableHeaderCell>Events</TableHeaderCell>
              <TableHeaderCell>Revenue</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>
                  <CustomerName>{customer.name}</CustomerName>
                  <CustomerEmail>{customer.email}</CustomerEmail>
                </TableCell>
                <TableCell>
                  <Badge color={customer.segmentColor}>
                    {customer.segment}
                  </Badge>
                </TableCell>
                <TableCell>{customer.events}</TableCell>
                <TableCell>\${customer.revenue.toLocaleString()}</TableCell>
                <TableCell>
                  <Link to={\`\${routePrefix}/customers/\${customer.id}\`}>
                    <ButtonV2 variant="icon" icon={<EyeIcon />} title="View" />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </Card>
  );
});

export default CustomerTable;`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default TablesDocs;
